/**
 * IntentRibbon — small contextual chip rendered ABOVE the SignupFlow
 * form when a visitor came from the public-shareable-link upsell
 * (public-shareable-link spec R3.3 + R3.4).
 *
 * Single-line; not dismissible (it's reassurance, not noise). The form
 * itself is unchanged. Renders null when no intent is present —
 * existing signup paths see exactly today's UI.
 */
import type { CSSProperties } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import type { IntentKind } from "./useIntent";

export interface IntentRibbonProps {
  intent: IntentKind | null;
  /** Resolved via GET /share/{source}/meta when intent=remix or save.
   *  Falls back to a generic line when null (network failure / loading). */
  sourceTitle: string | null;
}

export function IntentRibbon({ intent, sourceTitle }: IntentRibbonProps): JSX.Element | null {
  if (!intent) return null;
  let copy: string;
  if (intent === "remix") {
    copy = sourceTitle
      ? `Finishing your remix — ${sourceTitle} (your version) will be waiting`
      : "Finishing your remix — your version will be waiting";
  } else if (intent === "save") {
    copy = sourceTitle
      ? `Saving ${sourceTitle} so you can come back to it`
      : "Saving it so you can come back to it";
  } else if (intent === "avatar") {
    copy = "Sign up, then you're back in the project as you";
  } else {
    // intent === "make_own"
    copy = "Make your own world — picking up where you left off";
  }
  return (
    <div role="status" style={containerStyle}>
      <Icon name="sparkles" size={14} stroke={ChromeTokens.color.ink} />
      <span style={textStyle}>{copy}</span>
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: ChromeTokens.spacing.xs,
  padding: `${ChromeTokens.spacing.xs}px ${ChromeTokens.spacing.md}px`,
  background: ChromeTokens.color.sand,
  color: ChromeTokens.color.ink,
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  marginBottom: ChromeTokens.spacing.md,
  alignSelf: "flex-start",
};

const textStyle: CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 360,
};
